import React, { useEffect, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'

const LANGUAGES = [
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
  { code: 'it', name: 'Italian' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'ru', name: 'Russian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'en', name: 'English' },
]

const ROWS_COUNT = 50

export default function BulkGenerate() {
  const [themes, setThemes] = useState(Array(ROWS_COUNT).fill(''))
  const [targetLang, setTargetLang] = useState('fr')
  const [nativeLang, setNativeLang] = useState('English')
  const [length, setLength] = useState('short')
  const [isGenerating, setIsGenerating] = useState(false)
  const [results, setResults] = useState(null)
  const [statusUrl, setStatusUrl] = useState(null)

  const handleThemeChange = (index, value) => {
    const newThemes = [...themes]
    newThemes[index] = value
    setThemes(newThemes)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Filter out empty themes
    const validThemes = themes.filter(theme => theme.trim().length > 0)
    
    if (validThemes.length === 0) {
      alert('Please enter at least one theme or title.')
      return
    }

    setIsGenerating(true)
    setResults(null)
    setStatusUrl(null)

    let queued = false
    let queuedStatusUrl = null

    try {
      const response = await fetch('/admin/texts/bulk_create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          themes: validThemes,
          target_lang: targetLang,
          native_lang: nativeLang,
          length: length
        })
      })

      const contentType = response.headers.get('content-type') || ''
      const isJson = contentType.includes('application/json')

      if (!response.ok) {
        const bodyText = await response.text()
        throw new Error(`Bulk generation failed (${response.status}). ${isJson ? '' : bodyText.slice(0, 200)}`)
      }

      if (!isJson) {
        const bodyText = await response.text()
        throw new Error(`Bulk generation failed: expected JSON, got "${contentType}". ${bodyText.slice(0, 200)}`)
      }

      const data = await response.json()

      if (data.success) {
        setResults(data)
        if (data.queued && data.status_url) {
          queued = true
          queuedStatusUrl = data.status_url
          setStatusUrl(data.status_url)
          return
        }
        if (data.created_count > 0) {
          // Clear successfully created themes
          const createdThemes = new Set(data.created_texts.map(t => t.theme))
          const newThemes = themes.map(theme => 
            createdThemes.has(theme.trim()) ? '' : theme
          )
          setThemes(newThemes)
        }
      } else {
        alert('Bulk generation failed. Check console for details.')
      }
    } catch (error) {
      console.error('Bulk generation error:', error)
      alert(`Failed to generate texts. ${error?.message || 'Check console.'}`)
    } finally {
      // If we queued the work, keep the UI "busy" until polling completes.
      if (!queued && !queuedStatusUrl) setIsGenerating(false)
    }
  }

  useEffect(() => {
    if (!statusUrl) return

    let cancelled = false
    const interval = setInterval(async () => {
      try {
        const response = await fetch(statusUrl, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
          }
        })

        const contentType = response.headers.get('content-type') || ''
        if (!response.ok || !contentType.includes('application/json')) return

        const data = await response.json()
        if (cancelled) return

        setResults(prev => ({ ...(prev || {}), ...data }))

        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(interval)
          setIsGenerating(false)

          if (data.status === 'completed' && data.created_texts?.length > 0) {
            const createdThemes = new Set(data.created_texts.map(t => (t.theme || '').trim()))
            setThemes(prevThemes =>
              prevThemes.map(theme => (createdThemes.has(theme.trim()) ? '' : theme))
            )
          }
        }
      } catch (e) {
        // keep polling; network hiccups shouldn't break UX
      }
    }, 2000)

    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [statusUrl])

  const filledRowsCount = themes.filter(t => t.trim().length > 0).length

  return (
    <UserLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Bulk Text Generator</h1>
          <Link href="/admin/texts" className="text-gray-600 hover:text-gray-900">
            ← Back to Texts
          </Link>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6">
          <h2 className="text-lg font-semibold mb-4">Generation Settings</h2>
          
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Language</label>
              <select 
                value={targetLang}
                onChange={e => setTargetLang(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm border p-2"
                disabled={isGenerating}
              >
                {LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Translate To</label>
              <select 
                value={nativeLang}
                onChange={e => setNativeLang(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm border p-2"
                disabled={isGenerating}
              >
                <option>English</option>
                <option>French</option>
                <option>Spanish</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Length</label>
              <div className="flex gap-4">
                {['short', 'medium', 'long'].map(l => (
                  <label key={l} className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="length" 
                      value={l}
                      checked={length === l}
                      onChange={e => setLength(e.target.value)}
                      disabled={isGenerating}
                      className="text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="capitalize">{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-600">
            <p>• Each article will be created with an AI-generated image</p>
            <p>• All articles will be tagged as "Pending"</p>
            <p>• Filled rows: {filledRowsCount} / {ROWS_COUNT}</p>
          </div>
        </div>

        {results && (
          <div className={`mb-6 p-4 rounded-lg ${
            (results.errors || []).length > 0
              ? 'bg-yellow-50 border border-yellow-200' 
              : 'bg-green-50 border border-green-200'
          }`}>
            <h3 className="font-semibold mb-2">
              {results.status ? `Generation Status: ${results.status}` : 'Generation Results'}{' '}
              {typeof results.created_count === 'number' ? `— ${results.created_count} created` : ''}
            </h3>
            {typeof results.total === 'number' && typeof results.processed === 'number' && (
              <p className="text-sm text-gray-700 mb-2">
                Progress: {results.processed} / {results.total}
              </p>
            )}
            {results.queued && results.status_url && (
              <p className="text-sm text-gray-700 mb-2">
                Bulk generation queued. This page will auto-update as texts are created.
              </p>
            )}
            {(results.created_texts || []).length > 0 && (
              <div className="mb-2">
                <p className="text-sm font-medium mb-1">Successfully created:</p>
                <ul className="text-sm list-disc list-inside space-y-1">
                  {(results.created_texts || []).slice(0, 10).map((item, i) => (
                    <li key={i}>
                      <Link 
                        href={`/admin/texts/${item.text_id}/edit`} 
                        className="text-emerald-600 hover:underline"
                        target="_blank"
                      >
                        {item.theme} → {item.title}
                      </Link>
                    </li>
                  ))}
                  {(results.created_texts || []).length > 10 && (
                    <li className="text-gray-500">... and {(results.created_texts || []).length - 10} more</li>
                  )}
                </ul>
              </div>
            )}
            {(results.errors || []).length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1 text-red-700">Errors:</p>
                <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                  {(results.errors || []).map((error, i) => (
                    <li key={i}>
                      {error.theme}: {error.errors.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h2 className="text-lg font-semibold">Content Ideas / Titles</h2>
            <button
              type="submit"
              disabled={isGenerating || filledRowsCount === 0}
              className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isGenerating ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Generating...
                </>
              ) : (
                <>
                  ✨ Generate {filledRowsCount} Articles
                </>
              )}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-3 text-left text-sm font-medium text-gray-700 w-12">#</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Theme / Title</th>
                </tr>
              </thead>
              <tbody>
                {themes.map((theme, index) => (
                  <tr key={index} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-500">{index + 1}</td>
                    <td className="p-3">
                      <input
                        type="text"
                        value={theme}
                        onChange={e => handleThemeChange(index, e.target.value)}
                        placeholder={`Enter theme or title ${index + 1}...`}
                        disabled={isGenerating}
                        className="w-full rounded-md border-gray-300 shadow-sm border p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}




